import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — Wrapper qui déclenche une animation d'apparition premium
 * quand l'élément entre dans le viewport (Intersection Observer).
 *
 * Style d'inspiration : Stripe / Linear / Apple / Vercel / Framer.
 * Non-destructif : n'affecte ni le layout ni les styles existants.
 * L'élément apparaît avec :
 *   - translateY (20-30px)
 *   - opacity 0 → 1
 *   - blur 8px → 0
 *   - easing premium (cubic-bezier(0.22, 1, 0.36, 1) = "easeOutExpo")
 *   - durée 700ms (par défaut)
 *
 * Chaque animation ne se joue qu'une seule fois (via `hasRun` state).
 * Respecte `prefers-reduced-motion` : rend le contenu instantanément visible.
 *
 * Props:
 *   - delay : décalage ms (pour effet stagger entre sections)
 *   - y : distance de translation en px (défaut 24)
 *   - duration : durée ms (défaut 700)
 *   - as : élément HTML (défaut "div")
 *   - threshold : seuil IO (défaut 0.12)
 *   - once : ne joue qu'une seule fois (défaut true)
 */
export default function Reveal({
  children,
  delay = 0,
  y = 24,
  duration = 700,
  as = "div",
  className = "",
  threshold = 0.12,
  once = true,
  ...rest
}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const hasRun = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect prefers-reduced-motion
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      hasRun.current = true;
      return;
    }
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            hasRun.current = true;
            if (once) io.disconnect();
          } else if (!once && !hasRun.current) {
            setInView(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, once]);

  const Tag = as;
  const style = {
    transform: inView ? "translate3d(0,0,0)" : `translate3d(0, ${y}px, 0)`,
    opacity: inView ? 1 : 0,
    filter: inView ? "blur(0px)" : "blur(8px)",
    transition: `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, filter ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: inView ? "auto" : "transform, opacity, filter",
  };

  return (
    <Tag ref={ref} style={style} className={className} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * StaggerReveal — Applique un décalage progressif à ses enfants directs.
 * Chaque enfant apparaît avec un délai = index × step.
 *
 * Usage :
 *   <StaggerReveal step={80}>
 *     <Card />
 *     <Card />
 *   </StaggerReveal>
 */
export function StaggerReveal({ children, step = 80, initialDelay = 0, y = 20, duration = 650, as = "div", className = "", threshold = 0.1, ...rest }) {
  const kids = React.Children.toArray(children);
  const Tag = as;
  return (
    <Tag className={className} {...rest}>
      {kids.map((child, idx) => (
        <Reveal
          key={idx}
          delay={initialDelay + idx * step}
          y={y}
          duration={duration}
          threshold={threshold}
        >
          {child}
        </Reveal>
      ))}
    </Tag>
  );
}
